<script setup lang="ts">
import type { PropType } from 'vue';
import { useIntervalFn } from '@vueuse/core';
import { differenceInMilliseconds, parseISO } from 'date-fns';
import { getApiRoot } from '~/utilities/api';
import { formatXchPrice } from '~/utilities/chia-display-utils';
import { calculateOvertime } from '~/utilities/overtime';

const rops = defineProps({
  nft: { type: Object as PropType<any>, required: true },
});
const { nft } = toRefs(rops);

const isMinted = computed(() => nft.value.launcher_coin.spent_block_index);

const { data: offers, refresh } = await useFetch<any>(`${getApiRoot()}/nfts/${nft.value.id}/offers`);
const { data: dexieOffers } = await useFetch<any>(
  `https://api.dexie.space/v1/offers?offered_or_requested=${nft.value.encoded_id}`
);

const sellOffers = computed(() => {
  return offers?.value
    ?.filter((offer) => offer.side === 1)
    .filter((offer) => offer.status === 0)
    .sort((a, b) => (a.xch_price > b.xch_price ? 1 : -1));
});

const buyOffers = computed(() => {
  return offers?.value
    ?.filter((offer) => offer.side === 2)
    .filter((offer) => offer.status === 0)
    .sort((a, b) => (a.xch_price < b.xch_price ? 1 : -1));
});

const openDexieOffers = computed(() => {
  return dexieOffers.value.offers?.filter((offer) => !offers.value?.find((o) => o.id === offer.id));
});

const getActiveAuction = (offsetMinutes = 0) => {
  const offsetMillis = offsetMinutes * 60 * 1000;

  for (let auction of nft.value.auctions) {
    const start = parseISO(auction.start);
    const end = parseISO(auction.end);
    auction = { ...auction, start, end };

    const isInRegularTime =
      differenceInMilliseconds(start, new Date()) < offsetMillis &&
      differenceInMilliseconds(end, new Date()) > offsetMillis;

    const overtime = calculateOvertime(auction, offers.value || []);

    if (overtime.isInOvertime) {
      return { ...auction, end: overtime.end, isInOvertime: true };
    } else if (isInRegularTime) {
      return auction;
    }
  }
};

function fetchOffers() {
  refresh();
}

const activeAuction = ref(getActiveAuction());
const recentlyEndedAuction = ref(getActiveAuction(-60 * 12));
const counter = ref(0);
useIntervalFn(() => {
  const oldActiveAuction = activeAuction.value;
  const newActiveAuction = getActiveAuction();
  recentlyEndedAuction.value = getActiveAuction(-60 * 12);

  activeAuction.value = newActiveAuction;

  if (newActiveAuction?.end.getTime() !== oldActiveAuction?.end.getTime()) {
    fetchOffers();
  } else if (
    newActiveAuction?.end &&
    differenceInMilliseconds(newActiveAuction.end, new Date()) < 30 * 1000 &&
    counter.value % 4 === 0
  ) {
    fetchOffers();
  } else if (newActiveAuction && counter.value >= 30) {
    fetchOffers();
    counter.value = 0;
    return;
  } else {
    const recentlyEndedDiff = differenceInMilliseconds(recentlyEndedAuction.value?.end, new Date()) / 1000;
    if (recentlyEndedDiff < 0 && recentlyEndedDiff > -60 && counter.value % 4 === 0) {
      fetchOffers();
    }
  }
  counter.value += 1;
}, 1000);

const hasOffersOrAuction = computed(() => {
  return (
    (sellOffers.value && sellOffers.value.length > 0) ||
    (buyOffers.value && buyOffers.value.length > 0) ||
    (openDexieOffers.value && openDexieOffers.value.length > 0) ||
    activeAuction.value ||
    recentlyEndedAuction.value
  );
});

const highestBid = (auction) => {
  const { end } = calculateOvertime(auction, offers.value);
  return offers.value
    .filter((offer) => offer.xch_price >= auction.reserve_xch_price)
    .filter((offer) => differenceInMilliseconds(parseISO(offer.created_at), end) < 0)
    .filter((offer) => offer.side === 2) // bids only
    .filter((offer) => offer.status !== 3 && offer.status !== 6) // not cancelled or expired
    .sort((a, b) => b.xch_price - a.xch_price)[0];
};
</script>
<template>
  <div>
    <div v-if="hasOffersOrAuction">
      <div v-if="activeAuction" class="mb-8">
        <h3 class="mb-4 text-2xl font-bold">{{ $t('details.auction') }}</h3>
        <div
          class="flex flex-col gap-4 rounded-lg bg-white px-6 py-4 ring-1 ring-neutral-300 dark:bg-neutral-800 dark:ring-neutral-700"
        >
          <div v-if="activeAuction.description">
            <div class="flex flex-col">
              <span class="text-sm font-bold text-neutral-700 dark:text-neutral-200">{{
                $t('details.auction_details')
              }}</span>
              <div class="mt-1 whitespace-pre-wrap text-sm text-neutral-500 dark:text-neutral-400">
                {{ activeAuction.description }}
              </div>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-2">
            <div class="flex flex-col">
              <span class="text-sm font-bold text-neutral-700 dark:text-neutral-200">{{
                $t('details.highest_bid')
              }}</span>
              <div class="flex flex-1 items-center">
                <div v-if="highestBid(activeAuction)" class="text-xl font-bold lg:text-4xl">
                  {{ formatXchPrice(highestBid(activeAuction).xch_price, true) }}
                </div>
                <div v-else class="text-base text-neutral-400 dark:text-neutral-500 lg:text-xl">
                  {{
                    activeAuction.reserve_xch_price
                      ? $t('details.no_bid_reserve', { price: activeAuction.reserve_xch_price })
                      : $t('details.no_bid')
                  }}
                </div>
              </div>
            </div>
            <div class="flex flex-col justify-between">
              <span class="text-sm font-bold text-neutral-700 dark:text-neutral-200">{{
                $t(activeAuction.isInOvertime ? 'details.overtime_ends_in' : 'details.auction_ends_in')
              }}</span>
              <DetailsAuctionCountdown :end="activeAuction.end" />
            </div>
          </div>
          <div>
            <DetailsUploadOfferButton :nft="nft" :side="2"> Place Bid </DetailsUploadOfferButton>
          </div>
        </div>
        <div class="pt-2 text-xs text-neutral-500 dark:text-neutral-400">
          {{ $t('details.auction_disclaimer') }}
        </div>
      </div>
      <div v-else-if="recentlyEndedAuction" class="mb-8">
        <h3 class="mb-4 text-2xl font-bold">{{ $t('details.auction_ended') }}</h3>
        <div
          class="grid grid-cols-2 gap-2 rounded-lg bg-white px-6 py-4 ring-1 ring-neutral-300 dark:bg-black dark:ring-neutral-600"
        >
          <div class="flex flex-col">
            <span class="text-sm font-bold text-neutral-700 dark:text-neutral-200">{{
              $t('details.highest_bid')
            }}</span>
            <div class="flex flex-1 items-center">
              <NuxtLink
                v-if="highestBid(recentlyEndedAuction)"
                :to="`/offers/${highestBid(recentlyEndedAuction).id}`"
                class="text-xl font-bold lg:text-4xl"
              >
                {{ formatXchPrice(highestBid(recentlyEndedAuction).xch_price, true) }}
              </NuxtLink>
              <div v-else class="text-base text-neutral-400 dark:text-neutral-500 lg:text-xl">
                {{ $t('details.no_bid') }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="mb-8 flex flex-col gap-4 lg:flex-row">
        <DetailsTakeOfferButton v-if="sellOffers?.length > 0" :offer="sellOffers[0]" :nft="nft">
          {{ isMinted ? 'Buy Now' : 'Mint' }}
        </DetailsTakeOfferButton>
        <DetailsUploadOfferButton v-else :nft="nft" :side="1"> List for Sale </DetailsUploadOfferButton>
        <DetailsUploadOfferButton v-if="isMinted" :nft="nft" :side="2" :primary="false">
          {{ $t('details.uploadOffer') }}
        </DetailsUploadOfferButton>
      </div>
      <div v-if="sellOffers && sellOffers.length > 0" class="mb-8">
        <h3 class="mb-4 text-2xl font-bold">{{ $t('details.listings') }}</h3>
        <DetailsOffers :offers="sellOffers" :nft="nft" />
      </div>
      <div v-if="isMinted && buyOffers && buyOffers.length > 0" class="mb-8">
        <h3 class="mb-4 text-2xl font-bold">{{ $t('details.bids') }}</h3>
        <DetailsOffers :offers="buyOffers" :nft="nft" />
      </div>
      <div v-if="isMinted && openDexieOffers && openDexieOffers.length > 0" class="mb-8">
        <h3 class="mb-4 text-2xl font-bold">Dexie Offers</h3>
        <DetailsDexieOffers :offers="openDexieOffers" :nft="nft" />
      </div>
    </div>
    <div v-else class="">
      <h3 class="mb-2 text-2xl font-bold">{{ $t('details.noOffers') }}</h3>
      <p class="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
        {{ $t('details.uploadFirstOffer') }}
      </p>
      <div class="mt-8 flex flex-col gap-4 lg:flex-row">
        <DetailsUploadOfferButton :nft="nft" :side="1"> List for Sale </DetailsUploadOfferButton>
        <DetailsUploadOfferButton :nft="nft" :side="2" :primary="false">
          {{ $t('details.uploadOffer') }}
        </DetailsUploadOfferButton>
      </div>
    </div>
  </div>
</template>
